import React, { useState, useEffect } from "react";
import { useDarkMode } from "context/DarkModeProvider";
import ClassLayout from "layouts/ClassLayout";
import { useLanguage } from "../../context/LanguageProvider";
import "../../assets/css/Class.css";
import { fetchIsTeacher, getMyStudents, getStudentSubSaves } from "../../apiHandles/user_progress";
import * as XLSX from "xlsx";

const STRUCTURE_GROUPS = {
    "Tree": ["Binary Tree", "AVL Tree", "Heap"],
    "Graph": ["Directed", "Undirected"],
    "Lists": ["Singly Linked List", "Doubly Linked List"],
    "Array": ["1D Array", "2D Array", "Dynamic Array"],
    "Stack": ["Array-based Stack", "Linked List Stack"],
    "Queue": ["Circular Queue", "Priority Queue"],
    "HashTable": ["Chaining", "Open  Addressing"],
    "Sets": ["Hash Set", "Tree Set"],
};

const ScoreCell = ({ value, isParent = false, darkMode, groupStats = null, isTotalRow = false }) => {
    const themeClass = darkMode ? "dark" : "";

    if (isParent && groupStats) {
        return (
            <td className={`score-parent-cell ${themeClass} ${isTotalRow ? "score-total-row" : ""}`}>
                <div className="score-group-text">
                    {isTotalRow
                        ? `${groupStats.started} / ${groupStats.completed}`
                        : `${groupStats.started} / ${groupStats.completed} / ${groupStats.total}`}
                </div>
            </td>
        );
    }

    const isDone = value === 100;
    const valueClass = isTotalRow ? "score-value-total" : (isDone ? "score-value-done" : (value > 0 ? "score-value-progress" : "score-value-empty"));

    return (
        <td className={`score-cell ${themeClass} ${isTotalRow ? "score-total-row" : ""}`}>
            <div className={`score-value ${valueClass} ${themeClass}`}>
                {isTotalRow ? value : (value !== null ? `${value}%` : "—")}
            </div>
            {!isTotalRow && value !== null && (
                <div className="score-bar-track">
                    <div className={`score-bar-fill ${isDone ? "score-bar-fill--done" : ""}`} style={{ width: `${value}%` }} />
                </div>
            )}
        </td>
    );
};

const LevelBadge = ({ level, darkMode }) => (
    <div className={`level-badge ${darkMode ? "dark" : ""}`}>{level}</div>
);

const StudentStatsTable = ({ students = [], darkMode }) => {
    const [expandedGroups, setExpandedGroups] = useState([]);
    const themeClass = darkMode ? "dark" : "";

    const toggleGroup = (group) => setExpandedGroups(prev => prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]);

    const getGroupStats = (studentScores, children) => {
        let started = 0; let completed = 0;
        children.forEach(child => {
            const score = studentScores?.[child] || 0;
            if (score === 100) { completed++; started++; }
            else if (score > 0) { started++; }
        });
        return { started, completed, total: children.length };
    };

    const classTotals = (() => {
        const totals = { groups: {}, individual: {} };
        Object.entries(STRUCTURE_GROUPS).forEach(([group, children]) => {
            children.forEach(child => {
                let count = 0;
                students.forEach(s => { if ((s.scores?.[child] || 0) === 100) count++; });
                totals.individual[child] = count;
            });
            let groupStarted = 0; let groupCompleted = 0;
            students.forEach(s => {
                const stats = getGroupStats(s.scores, children);
                groupStarted += stats.started;
                groupCompleted += stats.completed;
            });
            totals.groups[group] = { started: groupStarted, completed: groupCompleted };
        });
        return totals;
    })();

    return (
        <div className={`stats-table-wrapper ${themeClass}`}>
            <table className="stats-table">
                <thead>
                    <tr className={`stats-header-row ${themeClass}`}>
                        <th className={`sticky-col sticky-left sticky-header sticky-header--row1 ${themeClass}`}>Student</th>
                        {Object.entries(STRUCTURE_GROUPS).map(([group, children]) => (
                            <th key={group}
                                colSpan={expandedGroups.includes(group) ? children.length : 1}
                                onClick={() => toggleGroup(group)}
                                className={`group-header ${themeClass} ${expandedGroups.includes(group) ? "group-header--expanded" : ""}`}>
                                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                    {group}
                                    <span style={{
                                        fontSize: '8px',
                                        opacity: 0.6,
                                        transition: 'transform 0.2s ease',
                                        display: 'inline-block',
                                        transform: expandedGroups.includes(group) ? 'rotate(180deg)' : 'rotate(0deg)'
                                    }}>▼</span>
                                </span>
                            </th>
                        ))}
                        <th className={`sticky-header sticky-small sticky-header--row1 ${themeClass}`}>Lvl</th>
                    </tr>
                    <tr className={`stats-header-row stats-header-row--sub ${themeClass}`}>
                        <th className={`sticky-col sticky-left sub-header-phantom ${themeClass}`} />
                        {Object.entries(STRUCTURE_GROUPS).map(([group, children]) => (
                            expandedGroups.includes(group) ?
                                children.map(child => <th key={child} className={`sub-header ${themeClass}`}>{child}</th>) :
                                <th key={group} className={`sub-header sub-header--stats ${themeClass}`}>S / C / A</th>
                        ))}
                        <th className={`sub-header-phantom ${themeClass}`} />
                    </tr>
                </thead>
                <tbody>
                    {students.map((student, idx) => (
                        <tr key={student.id} className={`student-row ${idx % 2 !== 0 ? (darkMode ? "student-row--alt-dark" : "student-row--alt") : ""} ${darkMode ? "student-row--alt-dark" : ""}`}>
                            <td className={`sticky-col sticky-left student-name-cell ${themeClass}`}>{student.username}</td>
                            {Object.entries(STRUCTURE_GROUPS).map(([group, children]) => {
                                if (expandedGroups.includes(group)) {
                                    return children.map(ds => <ScoreCell key={ds} value={student.scores?.[ds] ?? null} darkMode={darkMode} />);
                                }
                                return <ScoreCell key={group} isParent={true} groupStats={getGroupStats(student.scores, children)} darkMode={darkMode} />;
                            })}
                            <td className={`level-cell sticky-small ${themeClass}`} style={{ borderBottom: darkMode ? '1px solid var(--border-color,#334155)' : '1px solid #e5e7eb', textAlign: 'center' }}>
                                <LevelBadge level={student.level} darkMode={darkMode} />
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="stats-tfoot">
                    <tr className={`totals-row ${themeClass}`}>
                        <td className={`sticky-col sticky-left totals-label ${themeClass}`}>CLASS TOTALS</td>
                        {Object.entries(STRUCTURE_GROUPS).map(([group, children]) => {
                            if (expandedGroups.includes(group)) {
                                return children.map(ds => <ScoreCell key={ds} value={classTotals.individual[ds]} isTotalRow={true} darkMode={darkMode} />);
                            }
                            return <ScoreCell key={group} isParent={true} groupStats={classTotals.groups[group]} isTotalRow={true} darkMode={darkMode} />;
                        })}
                        <td style={{ color: "white", textAlign: "center" }}>—</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

const ClassPage = () => {
    const { darkMode } = useDarkMode();
    const { selLanguage } = useLanguage();
    const [isTeacher, setIsTeacher] = useState(null);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => { fetchIsTeacher().then(setIsTeacher); }, []);
    useEffect(() => {
        if (isTeacher) {
            setLoading(true);
            getMyStudents().then(async (list) => {
                const results = await Promise.all(list.map(async (s) => {
                    const saves = await getStudentSubSaves(s.username);
                    const scores = {};
                    saves.forEach(sv => scores[sv.sub_structure_name__name] = sv.progression);
                    return { ...s, scores };
                }));
                setStudents(results);
                setLoading(false);
            });
        }
    }, [isTeacher]);

    const handleDownload = () => {
        const wb = XLSX.utils.book_new();

        // Row 1: group headers, each merged across its children columns
        const headerRow1 = ["Student"];
        Object.entries(STRUCTURE_GROUPS).forEach(([group, children]) => {
            headerRow1.push(group);
            children.slice(1).forEach(() => headerRow1.push(""));
        });
        headerRow1.push("Level");

        // Row 2: sub-structure names
        const headerRow2 = [""];
        Object.values(STRUCTURE_GROUPS).forEach((children) => {
            children.forEach((child) => headerRow2.push(child));
        });
        headerRow2.push("");

        // Data rows: one per student
        const dataRows = students.map((student) => {
            const row = [student.username];
            Object.values(STRUCTURE_GROUPS).forEach((children) => {
                children.forEach((child) => {
                    const val = student.scores?.[child] ?? null;
                    row.push(val !== null ? val : "—");
                });
            });
            row.push(student.level ?? "—");
            return row;
        });

        // Totals row: count of students who completed (100%) each sub-structure
        const totalsRow = ["CLASS TOTALS"];
        Object.values(STRUCTURE_GROUPS).forEach((children) => {
            children.forEach((child) => {
                let count = 0;
                students.forEach((s) => { if ((s.scores?.[child] || 0) === 100) count++; });
                totalsRow.push(count);
            });
        });
        totalsRow.push("—");

        const wsData = [headerRow1, headerRow2, ...dataRows, totalsRow];
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Merge group header cells across their children columns
        ws["!merges"] = [];
        let colIndex = 1; // column 0 = "Student"
        Object.values(STRUCTURE_GROUPS).forEach((children) => {
            if (children.length > 1) {
                ws["!merges"].push({
                    s: { r: 0, c: colIndex },
                    e: { r: 0, c: colIndex + children.length - 1 },
                });
            }
            colIndex += children.length;
        });

        const allRows = [headerRow1, headerRow2, ...dataRows, totalsRow];
        const colWidths = allRows.reduce((acc, row) => {
            row.forEach((cell, i) => {
                const len = cell != null ? String(cell).length : 0;
                acc[i] = Math.max(acc[i] || 8, len + 2);
            });
            return acc;
        }, []);
        ws["!cols"] = colWidths.map(w => ({ wch: w }));

        XLSX.utils.book_append_sheet(wb, ws, "Student Analytics");
        XLSX.writeFile(wb, "student_analytics.xlsx");
    };

    return (
        <ClassLayout>
            <div className="class-page-container">
                <div className="class-page-header">
                    <h3 className={`class-page-title ${darkMode ? "dark" : ""}`}>
                        {!selLanguage ? "Student Analytics" : "Αναλυτικά Φοιτητών"}
                        <span className="sca-info-wrapper">
                            <span className="sca-info-icon">ⓘ</span>
                            <div className={`sca-info-tooltip ${darkMode ? "dark" : ""}`}>
                                <div><strong>S:</strong> {!selLanguage ? "How many substructures has the student started" : "Πόσα υποδομές έχει ξεκινήσει ο φοιτητής"} </div>
                                <div><strong>C:</strong> {!selLanguage ? "How many substructures has the student completed" : "Πόσες υποδομές έχει ολοκληρώσει ο φοιτητής"} </div>
                                <div><strong>A:</strong> {!selLanguage ? "How many substructures exist" : "Πόσες υποδομές υπάρχουν"} </div>
                            </div>
                        </span>
                        <button className="download-button" onClick={handleDownload} style={{ color: darkMode ? "#a0aec0" : "#131212d2" }}>
                            Download <i className="fas fa-cloud-download-alt"></i>
                        </button>
                    </h3>
                </div>
                {loading ? <p className="class-page-loading">Loading statistics...</p> : <StudentStatsTable students={students} darkMode={darkMode} />}
            </div>
        </ClassLayout>
    );
};

export default ClassPage;